import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormInput, FormSelect } from 'view/components/Form';
import { STALE_ROW_TYPES, STATUS_OPTIONS } from './emailTrackingLinks';

const SMS_STATUS_FIELD = 'sms_status';
const SMS_FILTER_TEST_ID = 'admin-email-sms-filter';
const DELIVERY_STATUS_FIELD = 'delivery_status';
const DEFAULT_SELECT_VALUE = 'all';

/**
 * True only when the backend advertises an SMS status filter on
 * GET /api/admin/email-tracking/filter-options. The frontend keeps no list of
 * its own: a control the backend cannot validate is a filter that silently
 * does nothing, so the capability signal has to come from the backend.
 */
const hasAdvertisedSmsFilter = (filterOptions) =>
	Array.isArray(filterOptions?.sms_statuses) &&
	filterOptions.sms_statuses.length > 0;

/**
 * True only when the backend advertises the delivery filter (what Amazon SES
 * reported for each email), for the same reason as the SMS filter.
 */
const hasAdvertisedDeliveryFilter = (filterOptions) =>
	Array.isArray(filterOptions?.delivery_statuses) &&
	filterOptions.delivery_statuses.length > 0;

/**
 * Builds the react-hook-form value set. The sms_status key is present only
 * when the backend advertises the filter, so a submitted payload never carries
 * a parameter the backend would drop.
 */
const buildFormValues = (filters, smsFilterAvailable, deliveryFilterAvailable) => {
	const values = {
		search: filters.search || '',
		status: filters.status || DEFAULT_SELECT_VALUE,
		type: filters.type || DEFAULT_SELECT_VALUE,
		date_from: filters.date_from || '',
		date_to: filters.date_to || '',
		older_than_hours: filters.older_than_hours || '',
	};

	if (smsFilterAvailable) {
		values[SMS_STATUS_FIELD] =
			filters[SMS_STATUS_FIELD] || DEFAULT_SELECT_VALUE;
	}

	if (deliveryFilterAvailable) {
		values[DELIVERY_STATUS_FIELD] =
			filters[DELIVERY_STATUS_FIELD] || DEFAULT_SELECT_VALUE;
	}

	return values;
};

const EmailFilters = ({
	filters,
	filterOptions,
	onFilterChange,
	onSearch,
	onReset,
}) => {
	const smsFilterAvailable = hasAdvertisedSmsFilter(filterOptions);
	const deliveryFilterAvailable = hasAdvertisedDeliveryFilter(filterOptions);

	const smsStatusOptions = useMemo(
		() => (smsFilterAvailable ? filterOptions.sms_statuses : []),
		[smsFilterAvailable, filterOptions]
	);

	const deliveryStatusOptions = useMemo(
		() => (deliveryFilterAvailable ? filterOptions.delivery_statuses : []),
		[deliveryFilterAvailable, filterOptions]
	);

	// React Hook Form setup
	const { control, handleSubmit, reset, watch } = useForm({
		defaultValues: buildFormValues(filters, smsFilterAvailable, deliveryFilterAvailable),
		mode: 'onChange',
	});

	// Watch form values and update parent filters
	const formValues = watch();

	useEffect(() => {
		// Update parent filters when form values change
		Object.keys(formValues).forEach((key) => {
			if (formValues[key] !== filters[key]) {
				// onFilterChange(key, formValues[key]);
			}
		});
	}, [filters, formValues, onFilterChange]);

	// Sync form with external filter changes and with the advertised capability
	useEffect(() => {
		reset(buildFormValues(filters, smsFilterAvailable, deliveryFilterAvailable));
	}, [filters, smsFilterAvailable, deliveryFilterAvailable, reset]);

	// Form submission handler
	const onSubmitForm = (data) => {
		onSearch(data);
	};

	// Reset handler
	const handleReset = useCallback(() => {
		reset(buildFormValues({}, smsFilterAvailable, deliveryFilterAvailable));
		onReset();
	}, [reset, smsFilterAvailable, deliveryFilterAvailable, onReset]);

	// Status options. Bounces are not a status but what SES reported: see the Delivery filter.
	const statusOptions = STATUS_OPTIONS;

	// Type options
	const typeOptions = [
		{ value: 'all', label: 'All Types' },
		{ value: 'alert', label: 'Alert' },
		{ value: 'notification', label: 'Notification' },
		{ value: 'expiring1', label: 'Expiring in 1 Day' },
		{ value: 'expiring7', label: 'Expiring in 7 Days' },
		{ value: 'expiring14', label: 'Expiring in 14 Days' },
		{ value: 'reminder', label: 'Reminder' },
		// The Stale rows card links this comma list; the option lets the select
		// show it instead of All Types
		{
			value: STALE_ROW_TYPES.join(','),
			label: `Stale types (${STALE_ROW_TYPES.length})`,
		},
	];

	return (
		<div className="card mb-4">
			<div className="card-header">
				<h6 className="mb-0">
					<i className="bi bi-funnel me-2" />
					Filters
				</h6>
			</div>
			<div className="card-body">
				<form onSubmit={handleSubmit(onSubmitForm)}>
					<div className="row g-3">
						{/* Search Input */}
						<div className="col-md-4">
							<FormInput
								name="search"
								control={control}
								label="Search"
								placeholder="Email, name, subject..."
								helpText="Search by email address, user name, or subject"
							/>
						</div>

						{/* Status Filter */}
						<div className="col-md-2">
							<FormSelect
								name="status"
								control={control}
								label="Status"
								options={statusOptions}
								placeholder={null}
							/>
						</div>

						{/* Type Filter */}
						<div className="col-md-2">
							<FormSelect
								name="type"
								control={control}
								label="Type"
								options={typeOptions}
								placeholder={null}
							/>
						</div>

						{/* SMS Status Filter - only when the backend advertises it */}
						{smsFilterAvailable && (
							<div className="col-md-2" data-testid={SMS_FILTER_TEST_ID}>
								<FormSelect
									name={SMS_STATUS_FIELD}
									control={control}
									label="SMS"
									options={smsStatusOptions}
									placeholder={null}
								/>
							</div>
						)}

						{/* Delivery Filter - what Amazon SES reported, only when the backend advertises it */}
						{deliveryFilterAvailable && (
							<div className="col-md-2">
								<FormSelect
									name={DELIVERY_STATUS_FIELD}
									control={control}
									label="Delivery"
									options={deliveryStatusOptions}
									placeholder={null}
								/>
							</div>
						)}

						{/* Date From */}
						<div className="col-md-2">
							<FormInput
								name="date_from"
								control={control}
								type="date"
								label="Date From"
							/>
						</div>

						{/* Date To */}
						<div className="col-md-2">
							<FormInput
								name="date_to"
								control={control}
								type="date"
								label="Date To"
							/>
						</div>

						{/* Older than: the outbox health cards link with it */}
						<div className="col-md-2">
							<FormInput
								name="older_than_hours"
								control={control}
								type="number"
								min={1}
								step={1}
								label="Older Than (hours)"
							/>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="d-flex gap-2 mt-3">
						<button type="submit" className="btn btn-primary">
							<i className="bi bi-search me-2" />
							Search
						</button>
						<button
							type="button"
							className="btn btn-secondary"
							onClick={handleReset}
						>
							<i className="bi bi-arrow-counterclockwise me-2" />
							Reset
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EmailFilters;
